import { useState } from 'react'
import { useParams } from 'react-router-dom'

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf']
const MAX_BYTES     = 10 * 1024 * 1024

export default function ReVerifyUploadPage() {
  const { token } = useParams()
  const [files, setFiles] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const onPick = (e) => {
    const picked = Array.from(e.target.files || [])
    const issues = []
    picked.forEach((f) => {
      if (!ALLOWED_TYPES.includes(f.type)) issues.push(`${f.name}: only .jpg / .png / .pdf allowed`)
      if (f.size > MAX_BYTES)              issues.push(`${f.name}: exceeds 10MB`)
    })
    if (issues.length) {
      setError(issues.join(' · '))
      setFiles([])
    } else {
      setError('')
      setFiles(picked)
    }
  }

  const submit = async (e) => {
    e.preventDefault()
    if (files.length === 0) return

    setSubmitting(true)
    setError('')

    const formData = new FormData()
    files.forEach((f) => formData.append('documents[]', f))

    try {
      const res = await fetch(`/api/v1/re_verifications/${encodeURIComponent(token)}/documents`, {
        method: 'POST',
        body: formData
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `Upload failed: ${res.status}`)
      setSuccess(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (success) {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 py-12">
        <div className="card p-6 sm:p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 mb-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Thanks — we got your documents</h1>
          <p className="text-sm text-slate-600">
            Our team will review the updated documentation and get back to you by email.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-12">
      <div className="card p-6 sm:p-8">
        <h1 className="text-xl font-semibold text-slate-900 mb-2">Upload re-verification documents</h1>
        <p className="text-sm text-slate-600 mb-6">
          Please upload your updated documentation (.jpg, .png, or .pdf — max 10MB per file). You can
          add multiple files.
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-800">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label" htmlFor="files">Files</label>
            <input
              id="files"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
              onChange={onPick}
              className="block w-full text-sm text-slate-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
              required
            />
            {files.length > 0 && (
              <ul className="mt-2 text-xs text-slate-600 space-y-0.5">
                {files.map((f) => (
                  <li key={f.name}>{f.name} <span className="text-slate-400">· {(f.size / 1024).toFixed(0)} KB</span></li>
                ))}
              </ul>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting || files.length === 0}
            className="btn-primary w-full"
          >
            {submitting ? 'Uploading…' : `Submit ${files.length || ''} file${files.length === 1 ? '' : 's'}`}
          </button>
        </form>

        <p className="text-xs text-slate-400 mt-6 text-center">
          This link is single-use and expires in 30 days.
        </p>
      </div>
    </div>
  )
}
